/**
 * Account menu: Profile panel (avatar, identity, theme, language, note),
 * Account → /account, the reseller-level Agreement → /agreement and Settlements →
 * /settlements (H §7.4, TMMRSL-0031, 0045), Sign out.
 */
import type { KeyboardEvent } from 'react';
import { Box, Divider, Menu, MenuItem } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '@/api/auth';
import { useAuth } from '@/auth/authHooks';
import { ProfilePanel } from './ProfilePanel';

export interface AccountMenuProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
}

const stopKeys = (event: KeyboardEvent<HTMLDivElement>) => {
  if (event.key !== 'Escape') event.stopPropagation();
};

export function AccountMenu({ anchorEl, onClose }: AccountMenuProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();

  async function handleSignOut() {
    onClose();
    try {
      await authApi.logout();
    } catch {
      /* best-effort — never blocks clearing the local session */
    }
    signOut();
    navigate('/auth/signed-out', { replace: true });
  }

  return (
    <Menu
      anchorEl={anchorEl}
      open={anchorEl !== null}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      // The `role="menu"` list carries the id the shell's trigger names in `aria-controls`.
      slotProps={{ paper: { sx: { minWidth: 300 } }, list: { id: 'account-menu' } }}
    >
      <Box sx={{ px: 1.5, pt: 1.5, pb: 1 }} onKeyDown={stopKeys}>
        <ProfilePanel />
      </Box>
      <Divider />
      <MenuItem
        onClick={() => {
          onClose();
          void navigate('/account');
        }}
      >
        {t('shell.account')}
      </MenuItem>
      <MenuItem
        onClick={() => {
          onClose();
          void navigate('/agreement');
        }}
      >
        {t('nav.agreement')}
      </MenuItem>
      <MenuItem
        onClick={() => {
          onClose();
          void navigate('/settlements');
        }}
      >
        {t('nav.settlements')}
      </MenuItem>
      <Divider />
      <MenuItem onClick={() => void handleSignOut()}>{t('shell.signOut')}</MenuItem>
    </Menu>
  );
}
